/**
 * Single row in the My Tasks list. Renders both WBS rows and migration
 * tasks via the unified `MyTaskItem` shape — see
 * `web/src/lib/myTasks.ts` for the source-merging logic.
 *
 * Interactions surfaced from this row:
 *   - tap / click → opens the source-aware detail panel (parent wires
 *     this through `onSelect`)
 *   - "✓ 完了" button → marks the row done (WBS only)
 *   - progress bar click → +10% (Shift = -10%, WBS only)
 *   - left-swipe → +10% progress (mobile, WBS only)
 *   - right-swipe → mark done (mobile, WBS only)
 *
 * Migration tasks expose only the open / details affordance — their
 * state lives per-run in `ExecutionDoc`, which is out of scope here.
 */
import { useRef, useState } from 'react';
import { Check, Layers, Wrench } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { statusDotClass } from '../../lib/wbsDependencyPicker';
import type { BlockedByMeRow, MyTaskItem } from '../../lib/myTasks';

/** Px the user has to drag horizontally before we treat it as a swipe. */
const SWIPE_THRESHOLD_PX = 60;

export interface MyTaskRowProps {
  row: MyTaskItem | BlockedByMeRow;
  selected: boolean;
  onSelect: () => void;
  /** Apply a `delta` (e.g. +10 / -10) to `row.progress`. WBS rows only. */
  onAdjustProgress?: (delta: number) => void | Promise<void>;
  /** Mark the row done. WBS rows only. */
  onComplete?: () => void | Promise<void>;
}

export function MyTaskRow({
  row,
  selected,
  onSelect,
  onAdjustProgress,
  onComplete,
}: MyTaskRowProps) {
  const isWbs = row.source === 'wbs';
  const successorCount =
    'successorCount' in row ? row.successorCount : 0;
  const touchStartXRef = useRef<number | null>(null);
  const [swipeFeedback, setSwipeFeedback] = useState<
    null | 'progress' | 'complete'
  >(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartXRef.current;
    touchStartXRef.current = null;
    if (start == null) return;
    const endX = e.changedTouches[0]?.clientX ?? start;
    const dx = endX - start;
    if (!isWbs) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (dx < 0) {
      // Left swipe: bump progress
      setSwipeFeedback('progress');
      void onAdjustProgress?.(10);
    } else {
      // Right swipe: complete
      setSwipeFeedback('complete');
      void onComplete?.();
    }
    window.setTimeout(() => setSwipeFeedback(null), 600);
  };

  const onProgressClick = (e: React.MouseEvent) => {
    if (!isWbs || !onAdjustProgress) return;
    e.stopPropagation();
    const delta = e.shiftKey ? -10 : 10;
    void onAdjustProgress(delta);
  };

  return (
    <li
      data-testid="my-task-row"
      data-source={row.source}
      data-row-id={row.id}
      className={cn(
        'group relative flex flex-col gap-1 border-b border-slate-100 bg-white px-3 py-2.5 text-sm transition-colors',
        selected ? 'bg-sky-50' : 'hover:bg-slate-50',
        swipeFeedback === 'progress' && 'bg-amber-50',
        swipeFeedback === 'complete' && 'bg-emerald-50',
      )}
      onClick={onSelect}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-block h-2 w-2 shrink-0 rounded-full',
            statusDotClass(row.status),
          )}
          aria-hidden
        />
        {/*
         * Phase 2.3: clamp the title to 2 lines instead of single-line
         * ellipsis. The list is only ~480px wide on a phone, so a
         * truncated 18-char prefix loses too much context. `title` is
         * still useful for hover-capable pointers (tablet stylus,
         * cursor-equipped phones) and is what the WBS row also surfaces.
         */}
        <span
          className="min-w-0 flex-1 break-words font-medium leading-snug text-slate-900 line-clamp-2"
          title={row.name}
        >
          {row.name}
        </span>
        {row.endDate ? (
          <span className="shrink-0 text-xs tabular-nums text-slate-500">
            期限: {format(row.endDate, 'M/d')}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5',
            isWbs
              ? 'bg-blue-50 text-blue-700'
              : 'bg-purple-50 text-purple-700',
          )}
          title={isWbs ? 'WBS 行' : '移行作業タスク'}
        >
          {isWbs ? (
            <Layers className="h-3 w-3" aria-hidden />
          ) : (
            <Wrench className="h-3 w-3" aria-hidden />
          )}
          {isWbs ? 'WBS' : '移行作業'}
        </span>
        {row.area ? <span className="truncate">{row.area}</span> : null}
        {row.vendor ? <span className="truncate">{row.vendor}</span> : null}
        {successorCount > 0 ? (
          <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
            待っている後続 {successorCount} 件
          </span>
        ) : null}
        {isWbs ? (
          // Phase 2.3: the visible progress strip is intentionally thin
          // (12px), but a 12px-tall button is hard to thumb. We wrap the
          // strip in a transparent button that inflates to a 44px hit
          // slab on mobile / tablet (`max-lg:py-4`) while the desktop
          // button stays at the original height.
          <button
            type="button"
            onClick={onProgressClick}
            data-testid="my-task-progress"
            className="ml-auto flex w-24 items-center bg-transparent p-0 max-lg:min-h-[44px] max-lg:min-w-[44px] max-lg:py-[16px]"
            aria-label={`進捗 ${row.progress}% (クリックで +10%)`}
            title="クリック: +10% / Shift+クリック: -10%"
          >
            <span className="block h-3 w-full overflow-hidden rounded-full bg-slate-200">
              <span
                className="block h-full bg-sky-500 transition-[width]"
                style={{ width: `${row.progress}%` }}
              />
            </span>
          </button>
        ) : null}
        {isWbs ? (
          <span className="tabular-nums text-slate-600">{row.progress}%</span>
        ) : null}
        {isWbs && onComplete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void onComplete();
            }}
            data-testid="my-task-complete"
            // Touch-target floor: tiny `py-0.5` chip on desktop is fine
            // for cursor users, but a thumb needs ≥44px. The min sizing
            // only applies below `lg`.
            className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-white px-2 py-0.5 text-emerald-700 hover:bg-emerald-50 max-lg:min-h-[44px] max-lg:min-w-[44px] max-lg:px-3"
            aria-label={`${row.name} を完了する`}
          >
            <Check className="h-3 w-3" /> 完了
          </button>
        ) : null}
      </div>
    </li>
  );
}
